import {
  CaseSummaryDTO,
} from '../models/CaseDTOs';
import {
  CaseReportSummary,
  CaseReport,
} from '../models/CaseTypes';
import CaseReportAdapter from '../models/CaseReportAdapter';
import ICaseReportService from './ICaseReportService';
import { getAppConfig } from '../utils/appConfig';
import { logError, logEvent } from '../utils/trackingUtils';
import { call } from '../utils/apiUtils';

interface CachedCaseReport {
  caseReport: CaseReport;
  cachedAt: Date;
}

export default class CaseReportService implements ICaseReportService {
  caseReportCache = new Map<string, CachedCaseReport>();
  caseReportAdapter = new CaseReportAdapter();
  config = getAppConfig();
  private readonly CACHE_EXPIRY_HOURS = 2;

  clearCache(): void {
    this.caseReportCache.clear();
  }

  private isCacheExpired(cachedAt: Date): boolean {
    const now = new Date();
    const diffInHours = (now.getTime() - cachedAt.getTime()) / (1000 * 60 * 60);
    return diffInHours >= this.CACHE_EXPIRY_HOURS;
  }

  async getServiceHealth(): Promise<object> {
    const url = `${this.config.CASE_API_URL}/api/v1/admin/dependencies`;

    // Pass requiresAuth: false for public endpoints
    const response = await call(url, { requiresAuth: false });

    let healthStatus;
    try {
      healthStatus = await response.json();
      logEvent({ context: 'getServiceHealth retrieved health info', healthStatus: healthStatus });
    } catch (error) {
      logError(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Service health parse error.');
    }
    return healthStatus;
  }

  /** Retrieves all details for a single case.
   * @param caseId The ID of the case to retrieve.
   * @returns CaseReport A promise that resolves to a CaseReport object.
   */
  async getCaseDetail(caseId: string): Promise<CaseReport> {
    if (this.caseReportCache.has(caseId)) {
      const cachedItem = this.caseReportCache.get(caseId) as CachedCaseReport;
      if (!this.isCacheExpired(cachedItem.cachedAt)) {
        return cachedItem.caseReport;
      } else {
        // Remove expired cache entry
        this.caseReportCache.delete(caseId);
      }
    }

    const url = `${this.config.CASE_API_URL}/api/v1/cases/${caseId}`;

    // call() fetches token and handles 401 automatically
    const response = await call(url);

    let caseDetail;
    try {
      caseDetail = await response.json();
      logEvent({ context: 'getCaseDetail retrieved case info.', caseId: caseId });
    } catch (error) {
      logError(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Case Detail parse error.');
    }

    const viewModel = this.caseReportAdapter.mapToViewModel(caseDetail);
    this.caseReportCache.set(caseId, {
      caseReport: viewModel,
      cachedAt: new Date()
    });
    return viewModel;
  }

  /**
   * Retrieve case summary data.
   *
   * @returns CaseReportSummary[] A promise that resolves to an array of CaseReportSummary objects.
   */
  async getCaseSummaries(): Promise<CaseReportSummary[]> {
    const url = `${this.config.CASE_API_URL}/api/v1/cases`;

    // call() fetches token and handles 401 automatically
    const response = await call(url);

    let caseSummaries;
    try {
      caseSummaries = (await response.json()).map((caseSummary: CaseSummaryDTO) => (
        this.caseReportAdapter.mapToSummaryViewModel(caseSummary)
      ));
      logEvent({ context: 'getCaseSummaries fetched cases', caseCount: caseSummaries.length });
    } catch (error) {
      logError(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Case summaries parse error.');
    }
    return caseSummaries;
  }
}
